import { useEffect, useRef, useState } from 'react';
import { EditorContent, useEditor, useEditorState, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Placeholder } from '@tiptap/extensions';

import { Icon, type IconName } from './Icon.tsx';
import { useI18n } from '~/i18n/index.tsx';

/**
 * The rich-text box used for page copy, policy sections and FAQ answers.
 *
 * Deliberately narrower than TipTap's defaults: the toolbar offers exactly what the server's
 * sanitiser allows through — paragraphs, two heading sizes, bold, italic, underline, lists,
 * quotes and links — and nothing else. An editor that lets someone insert an image or a code
 * block, only for the API to strip it on save, teaches staff not to trust the panel.
 *
 * `dir` and `lang` are set on the editable element itself, so the Arabic box types and renders
 * right-to-left whichever language the panel is in.
 */
export function RichText({
  value,
  onChange,
  lang,
  ariaLabel,
  placeholder,
}: {
  value: string;
  onChange: (html: string) => void;
  lang: 'ar' | 'en';
  ariaLabel: string;
  placeholder?: string;
}) {
  const { t } = useI18n();

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Nothing here survives the sanitiser, so nothing here is offered.
        codeBlock: false,
        code: false,
        horizontalRule: false,
        strike: false,
        heading: { levels: [2, 3, 4] },
        // StarterKit carries its own link. Registering a second one beside it is what printed
        // "Duplicate extension names found: ['link']" on every editor.
        link: {
          openOnClick: false,
          autolink: false,
          // Matches the API's allowed schemes exactly.
          protocols: ['http', 'https', 'mailto', 'tel'],
          HTMLAttributes: { rel: 'noopener noreferrer' },
        },
      }),
      Placeholder.configure({ placeholder: placeholder ?? t('content.bodyPlaceholder') }),
    ],
    content: value,
    editorProps: {
      attributes: {
        class: 'richtext__body',
        dir: lang === 'ar' ? 'rtl' : 'ltr',
        lang,
        'aria-label': ariaLabel,
        'aria-multiline': 'true',
        role: 'textbox',
      },
    },
    onUpdate: ({ editor: current }) => {
      // An empty editor still serialises as "<p></p>"; the API should be given nothing instead,
      // so an untouched field stays null rather than becoming an empty paragraph.
      onChange(current.isEmpty ? '' : current.getHTML());
    },
  });

  /*
   * Load a value that arrives after mount — the editor is rendered before the fetch resolves.
   * Guarded on the content actually differing, or every keystroke would reset the cursor to the
   * start of the box.
   */
  useEffect(() => {
    if (editor && !editor.isDestroyed && value !== editor.getHTML() && (value || !editor.isEmpty)) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!editor) {
    return <div className="richtext richtext--loading" aria-busy="true" />;
  }

  return (
    <div className="richtext">
      <Toolbar editor={editor} label={`${ariaLabel} — ${t('content.formatting')}`} />
      <EditorContent editor={editor} />
    </div>
  );
}

/**
 * Every toolbar control suppresses mousedown.
 *
 * Without it the browser moves focus to the button before the click handler runs, and the caret
 * — and the selection it was sitting in — is gone. Turning on bold and carrying on typing then
 * types into nothing, which is the single commonest way a toolbar like this is broken.
 */
const keepCaret = (event: React.MouseEvent) => event.preventDefault();

type Style = 'paragraph' | 'heading' | 'subheading';

/** What the toolbar shows while there is no editor to ask: nothing on, nothing to undo. */
const IDLE = {
  style: 'paragraph' as Style,
  bold: false,
  italic: false,
  underline: false,
  bulletList: false,
  orderedList: false,
  blockquote: false,
  link: false,
  href: '',
  canUndo: false,
  canRedo: false,
};

function Toolbar({ editor, label }: { editor: Editor; label: string }) {
  const { t } = useI18n();
  const [linking, setLinking] = useState(false);

  /*
   * TipTap 3 no longer re-renders the component on every transaction, so a toolbar reading
   * `editor.isActive` during render showed the state of the moment it mounted: bold stayed lit
   * after the caret left the bold word. This subscribes to exactly what the buttons show.
   */
  const state = useEditorState({
    editor,
    // While TipTap builds or tears an editor down (React mounts twice in development) the
    // instance exists without its command manager, and `.can()` on it threw — taking the whole
    // editor page down with it. Until it is whole, the toolbar shows nothing on.
    selector: ({ editor: current }) => (!current || current.isDestroyed || !current.isInitialized ? IDLE : {
      style: (current.isActive('heading', { level: 2 })
        ? 'heading'
        : current.isActive('heading')
          ? 'subheading'
          : 'paragraph') as Style,
      bold: current.isActive('bold'),
      italic: current.isActive('italic'),
      underline: current.isActive('underline'),
      bulletList: current.isActive('bulletList'),
      orderedList: current.isActive('orderedList'),
      blockquote: current.isActive('blockquote'),
      link: current.isActive('link'),
      href: (current.getAttributes('link').href as string | undefined) ?? '',
      canUndo: current.can().undo(),
      canRedo: current.can().redo(),
    }),
  });

  function setStyle(style: Style) {
    const chain = editor.chain().focus();

    if (style === 'paragraph') {
      chain.setParagraph().run();
    } else {
      chain.setHeading({ level: style === 'heading' ? 2 : 3 }).run();
    }
  }

  return (
    <div className="richtext__head">
      <div className="richtext__bar" role="toolbar" aria-label={label}>
        <select
          className="richtext__style"
          value={state.style}
          aria-label={t('content.textStyle')}
          onChange={(event) => setStyle(event.target.value as Style)}
        >
          <option value="paragraph">{t('content.styleParagraph')}</option>
          <option value="heading">{t('content.styleHeading')}</option>
          <option value="subheading">{t('content.styleSubheading')}</option>
        </select>

        <span className="richtext__sep" aria-hidden="true" />

        <Tool icon="bold" label={t('content.bold')} on={state.bold} run={() => editor.chain().focus().toggleBold().run()} />
        <Tool icon="italic" label={t('content.italic')} on={state.italic} run={() => editor.chain().focus().toggleItalic().run()} />
        <Tool
          icon="underline"
          label={t('content.underline')}
          on={state.underline}
          run={() => editor.chain().focus().toggleUnderline().run()}
        />

        <span className="richtext__sep" aria-hidden="true" />

        <Tool
          icon="listBullet"
          label={t('content.bulletList')}
          on={state.bulletList}
          run={() => editor.chain().focus().toggleBulletList().run()}
        />
        <Tool
          icon="listNumbered"
          label={t('content.numberedList')}
          on={state.orderedList}
          run={() => editor.chain().focus().toggleOrderedList().run()}
        />
        <Tool
          icon="quote"
          label={t('content.quote')}
          on={state.blockquote}
          run={() => editor.chain().focus().toggleBlockquote().run()}
        />

        <span className="richtext__sep" aria-hidden="true" />

        <Tool icon="link" label={t('content.link')} on={state.link || linking} run={() => setLinking((open) => !open)} />

        <span className="richtext__spacer" aria-hidden="true" />

        <Tool
          icon="undo"
          label={t('content.undo')}
          flip
          disabled={!state.canUndo}
          run={() => editor.chain().focus().undo().run()}
        />
        <Tool
          icon="redo"
          label={t('content.redo')}
          flip
          disabled={!state.canRedo}
          run={() => editor.chain().focus().redo().run()}
        />
      </div>

      {linking ? <LinkBar editor={editor} href={state.href} onDone={() => setLinking(false)} /> : null}
    </div>
  );
}

function Tool({
  icon,
  label,
  on = false,
  disabled = false,
  flip = false,
  run,
}: {
  icon: IconName;
  label: string;
  on?: boolean;
  disabled?: boolean;
  flip?: boolean;
  run: () => void;
}) {
  return (
    <button
      type="button"
      className={`richtext__tool ${on ? 'is-on' : ''}`}
      // Pressed, not just styled: a toolbar button whose state is only a colour is invisible to
      // anyone using a screen reader.
      aria-pressed={on}
      aria-label={label}
      title={label}
      disabled={disabled}
      onMouseDown={keepCaret}
      onClick={run}
    >
      <Icon name={icon} size={18} strokeWidth={1.8} className={flip ? 'rtl-flip' : undefined} />
    </button>
  );
}

/**
 * Where a link goes, typed under the toolbar rather than into `window.prompt`.
 *
 * The prompt was a browser dialog in the browser's language with a pre-filled `https://` to
 * select and delete, and no way to see or change an existing link short of removing it first.
 */
function LinkBar({ editor, href, onDone }: { editor: Editor; href: string; onDone: () => void }) {
  const { t } = useI18n();
  const [draft, setDraft] = useState(href);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => input.current?.focus(), []);

  function apply() {
    const target = draft.trim();

    if (target) {
      // A bare address is a web address: "awj.one" should not become a relative link.
      const withScheme = /^(https?:|mailto:|tel:|\/|#)/i.test(target) ? target : `https://${target}`;

      if (editor.state.selection.empty && !editor.isActive('link')) {
        // Nothing selected to turn into a link, so the address goes in as its own linked text —
        // otherwise Apply would arm a link for the next thing typed and appear to do nothing.
        editor
          .chain()
          .focus()
          .insertContent({ type: 'text', text: target, marks: [{ type: 'link', attrs: { href: withScheme } }] })
          .run();
      } else {
        editor.chain().focus().extendMarkRange('link').setLink({ href: withScheme }).run();
      }
    } else {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    }

    onDone();
  }

  return (
    <div className="richtext__link">
      <Icon name="link" size={16} />
      <input
        ref={input}
        className="richtext__linkInput"
        dir="ltr"
        type="url"
        value={draft}
        placeholder="https://"
        aria-label={t('content.linkPrompt')}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            apply();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            onDone();
            editor.commands.focus();
          }
        }}
      />
      <button type="button" className="richtext__linkAction" onClick={apply}>
        {t('content.linkApply')}
      </button>
      {href ? (
        <button
          type="button"
          className="richtext__linkAction richtext__linkAction--quiet"
          onClick={() => {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            onDone();
          }}
        >
          <Icon name="unlink" size={16} />
          {t('content.linkRemove')}
        </button>
      ) : null}
    </div>
  );
}
